import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  UserMinus, UserPlus, Calendar, User, LayoutGrid, List, ChevronLeft, ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useCrud } from '../../api/hooks/useCrud';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import type { Professor } from '../../types/models';
import { ROUTES } from '../../api/routes';
import { Dropdown } from '../../components/ui/Dropdown';
import { DatePicker } from '../../components/ui/DatePicker';
import { ConfirmModal } from '../../components/ui/ConfirmModal';

type Afastamento = {
  id: string;
  professorId: string;
  tipo: string;
  dataInicio: string;
  dataFim: string;
  status: string;
  motivo?: string | null;
  professor: { id: string; nome: string };
};

type AlertaCobertura = {
  id: string;
  dataAfetada: string;
  status: string;
  mensagemAlerta: string;
  afastamentoId?: string | null;
  horarioAulaId?: string;
  numeroAula: number;
  horaInicio?: string | null;
  horaFim?: string | null;
  diaSemana?: string | null;
  turma?: {
    id: string;
    nome: string;
    serie?: string | null;
    turnoOperacional?: { id: string; nome: string } | null;
  } | null;
  materia?: { id: string; nome: string } | null;
  titular?: { id: string; nome: string } | null;
  substituto?: { id: string; nome: string } | null;
};

type ViewMode = 'grade' | 'lista';

type TurnoKey = 'M' | 'T' | 'N' | 'X';

/** Slot da grade permanente do professor (seleção no form). */
type SlotGradeProf = {
  id: string;
  diaSemana: string;
  turnoKey: TurnoKey;
  turnoLabel: string;
  num: number;
  hora: string;
  horaFim: string;
  materia: string;
  corVisual?: string | null;
  turma: string;
  jaCoberto: boolean;
};

function parseHexColor(hex?: string | null): { r: number; g: number; b: number } | null {
  if (!hex) return null;
  const raw = hex.trim().replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function softMateriaBg(hex: string, whiteRatio = 0.7): string {
  const rgb = parseHexColor(hex);
  if (!rgb) return '#f8fafc';
  const mix = (c: number) => Math.round(c + (255 - c) * whiteRatio);
  return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
}

const TURNO_LABEL: Record<TurnoKey, string> = {
  M: 'Manhã',
  T: 'Tarde',
  N: 'Noite',
  X: 'Outros',
};

const DIAS_ORDEM = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'] as const;

const DIA_CURTO: Record<string, string> = {
  SEGUNDA: 'Seg',
  TERCA: 'Ter',
  QUARTA: 'Qua',
  QUINTA: 'Qui',
  SEXTA: 'Sex',
  SABADO: 'Sáb',
  DOMINGO: 'Dom',
};

/** Segunda da semana civil (UTC) para agrupar colunas. */
function mondayOf(ymd: string): string {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12, 0, 0, 0));
  const day = dt.getUTCDay(); // 0=Dom
  const diff = day === 0 ? -6 : 1 - day;
  dt.setUTCDate(dt.getUTCDate() + diff);
  return toYmdUtc(dt);
}

function toYmdUtc(dt: Date): string {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12, 0, 0, 0));
  dt.setUTCDate(dt.getUTCDate() + days);
  return toYmdUtc(dt);
}

function weekdayFromYmd(ymd: string): string {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  // Meio-dia UTC evita virar dia anterior/próximo no fuso local
  const js = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12, 0, 0, 0)).getUTCDay();
  const map = [
    'DOMINGO',
    'SEGUNDA',
    'TERCA',
    'QUARTA',
    'QUINTA',
    'SEXTA',
    'SABADO',
  ];
  return map[js] || '';
}

function labelTurma(a: AlertaCobertura): string {
  const nome = a.turma?.nome || 'Turma';
  const turno = a.turma?.turnoOperacional?.nome;
  return turno ? `${nome} · ${turno}` : nome;
}

type TurmaAfetada = {
  id: string;
  nome: string;
  turnoKey: TurnoKey;
  turnoNome: string;
  pendentes: number;
  cobertos: number;
};

const TURNO_BLOCOS: { key: TurnoKey; titulo: string; accent: string }[] = [
  { key: 'M', titulo: 'Manhã', accent: '#0369a1' },
  { key: 'T', titulo: 'Tarde', accent: '#c2410c' },
  { key: 'N', titulo: 'Noite', accent: '#6d28d9' },
  { key: 'X', titulo: 'Outros', accent: '#64748b' },
];

function turnoKeyFromNome(nome?: string | null): TurnoKey {
  const n = (nome || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
  if (n.includes('manh') || n === 'm') return 'M';
  if (n.includes('tard') || n === 't') return 'T';
  if (n.includes('noit') || n === 'n') return 'N';
  return 'X';
}

type Candidato = { id: string; nome: string };

type RankEncaixe = {
  id: string;
  nome: string;
  quantidade: number;
  encaixam: string[];
};

const TIPOS = [
  { value: 'IMPREVISTO', label: 'Imprevisto' },
  { value: 'SAUDE', label: 'Saúde' },
  { value: 'FERIAS', label: 'Férias' },
  { value: 'LICENCA_MATERNIDADE', label: 'Licença maternidade' },
  { value: 'OUTROS', label: 'Outros' },
];

function formatYmdBr(ymd?: string | null) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return ymd || '—';
  const [y, m, d] = ymd.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

const STATUS_LABEL: Record<string, string> = {
  PROGRAMADO: 'Programado',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
};

export function CoberturaGradePage() {
  const escolaId = useAuthStore((s) => s.escolaId);
  const { hojeStr, isSimulation } = useHojeReferencia();
  const { data: professores = [] } = useCrud<Professor>('professores');

  const [afastamentos, setAfastamentos] = useState<Afastamento[]>([]);
  const [alertas, setAlertas] = useState<AlertaCobertura[]>([]);
  const [loading, setLoading] = useState(false);
  /** Professores com pelo menos 1 slot AULA na grade da escola */
  const [profIdsComGrade, setProfIdsComGrade] = useState<Set<string>>(new Set());

  const [professorId, setProfessorId] = useState('');
  const [tipo, setTipo] = useState('IMPREVISTO');
  const [dataInicio, setDataInicio] = useState(hojeStr);
  const [dataFim, setDataFim] = useState(hojeStr);
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  /** IDs de `horarioAula` selecionados no form (grade real do professor). */
  const [slotsSelIds, setSlotsSelIds] = useState<string[]>([]);
  const [slotsGrade, setSlotsGrade] = useState<SlotGradeProf[]>([]);
  const [loadingAulas, setLoadingAulas] = useState(false);

  const [alertaSel, setAlertaSel] = useState<string | null>(null);
  const [candidatos, setCandidatos] = useState<Candidato[]>([]);
  const [subId, setSubId] = useState('');
  const [alocando, setAlocando] = useState(false);
  const [loteSubId, setLoteSubId] = useState('');
  const [loteRunning, setLoteRunning] = useState(false);
  /** Ranking do lote: só quem encaixa, com quantidade (maior → menor). */
  const [loteRanking, setLoteRanking] = useState<RankEncaixe[]>([]);
  const [loteRankingLoading, setLoteRankingLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grade');
  const [semanaInicio, setSemanaInicio] = useState('');
  /** Detalhe de manutenção de um afastamento (null = lista geral). */
  const [afastamentoDetalheId, setAfastamentoDetalheId] = useState<string | null>(null);
  /** null = Todos; senão filtra slots cobertos por esse substituto. */
  const [filtroSubstitutoId, setFiltroSubstitutoId] = useState<string | null>(null);
  const [revogando, setRevogando] = useState(false);
  const [confirmCancelarAfId, setConfirmCancelarAfId] = useState<string | null>(null);
  const [cancelandoAf, setCancelandoAf] = useState(false);

  const afDetalhe = useMemo(
    () => afastamentos.find((a) => a.id === afastamentoDetalheId) || null,
    [afastamentos, afastamentoDetalheId],
  );

  const profsAtivos = useMemo(
    () =>
      (professores ?? [])
        .filter((p) => p.ativo !== false)
        .slice()
        .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR')),
    [professores],
  );

  const professorOptions = useMemo(() => {
    const comGrade = profsAtivos.filter((p) => profIdsComGrade.has(p.id));
    const base = (comGrade.length ? comGrade : profsAtivos).map((p) => ({
      value: p.id,
      label: p.nome || p.id,
    }));
    return base;
  }, [profsAtivos, profIdsComGrade]);

  const tipoOptions = useMemo(
    () => TIPOS.map((t) => ({ value: t.value, label: t.label })),
    [],
  );

  const carregar = useCallback(
    async (opts?: { forceAll?: boolean }) => {
      if (!escolaId) return;
      setLoading(true);
      try {
        const useDetail = !opts?.forceAll && afDetalhe;
        const alertParams = useDetail
          ? {
              afastamentoId: useDetail.id,
              professorId: useDetail.professorId,
              de: useDetail.dataInicio.slice(0, 10),
              ate: (useDetail.dataFim || useDetail.dataInicio).slice(0, 10),
            }
          : undefined;
        const requests: Promise<unknown>[] = [
          api.get(ROUTES.cobertura.afastamentos(escolaId), {
            params: { hoje: hojeStr },
          }),
          api.get(ROUTES.cobertura.alertas(escolaId), { params: alertParams }),
        ];
        // Ocupação só na lista (filtro do form) — no detalhe evita request pesado
        if (!useDetail) {
          requests.push(
            api.get(ROUTES.horarios.ocupacao(escolaId)).catch(() => ({ data: null })),
          );
        }
        const [af, al, oc] = (await Promise.all(requests)) as [
          { data: unknown },
          { data: unknown },
          { data: unknown }?,
        ];
        setAfastamentos(Array.isArray(af.data) ? af.data : []);
        setAlertas(Array.isArray(al.data) ? al.data : []);

        if (!useDetail && oc) {
          const ocup = oc?.data as Record<string, unknown> | unknown[] | null;
          const ids = new Set<string>();
          const lista = Array.isArray(ocup)
            ? ocup
            : Array.isArray((ocup as { ocupacao?: unknown })?.ocupacao)
              ? (ocup as { ocupacao: unknown[] }).ocupacao
              : Array.isArray((ocup as { professores?: unknown })?.professores)
                ? (ocup as { professores: unknown[] }).professores
                : [];
          for (const item of lista as Array<{ professorId?: string; id?: string }>) {
            const id = item?.professorId || item?.id;
            if (id) ids.add(String(id));
          }
          setProfIdsComGrade(ids);
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Erro ao carregar cobertura');
      } finally {
        setLoading(false);
      }
    },
    [
      escolaId,
      hojeStr,
      afDetalhe?.id,
      afDetalhe?.professorId,
      afDetalhe?.dataInicio,
      afDetalhe?.dataFim,
    ],
  );

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    setFiltroSubstitutoId(null);
    setAlertaSel(null);
    setCandidatos([]);
    setSubId('');
  }, [afastamentoDetalheId]);

  useEffect(() => {
    setDataInicio(hojeStr);
    setDataFim(hojeStr);
  }, [hojeStr]);

  /** Carrega a grade semanal real do professor no período (dia × matéria). */
  useEffect(() => {
    if (!escolaId || !professorId || !dataInicio) {
      setSlotsGrade([]);
      setSlotsSelIds([]);
      return;
    }
    let cancelled = false;
    setLoadingAulas(true);
    void (async () => {
      try {
        const fim = (dataFim || dataInicio).slice(0, 10);
        const ini = dataInicio.slice(0, 10);
        const [horRes, alertRes] = await Promise.all([
          api.get(ROUTES.horarios.porEscola(escolaId), {
            params: { professorId },
          }),
          api
            .get(ROUTES.cobertura.alertas(escolaId), {
              params: { professorId, de: ini, ate: fim },
            })
            .catch(() => ({ data: [] })),
        ]);
        const rows = (
          Array.isArray(horRes.data) ? horRes.data : horRes.data?.data ?? []
        ) as Array<{
          id?: string;
          tipo?: string;
          numeroAula?: number;
          diaSemana?: string;
          horaInicio?: string | null;
          horaFim?: string | null;
          materia?: { nome?: string; corVisual?: string | null } | null;
          turma?: {
            nome?: string;
            turnoOperacional?: { nome?: string } | null;
          } | null;
        }>;
        const alertasExistentes = (
          Array.isArray(alertRes.data) ? alertRes.data : []
        ) as AlertaCobertura[];
        const cobertosIds = new Set<string>();
        for (const a of alertasExistentes) {
          if (a.status !== 'PENDENTE' && a.status !== 'SUBTITUTO_ALOCADO') continue;
          if (a.horarioAulaId) cobertosIds.add(a.horarioAulaId);
        }

        const diasSemanaNoPeriodo = new Set<string>();
        let cur = ini;
        while (cur <= fim) {
          const ds = weekdayFromYmd(cur);
          if (ds && ds !== 'DOMINGO') diasSemanaNoPeriodo.add(ds);
          cur = addDaysYmd(cur, 1);
        }

        const turnoOrd: TurnoKey[] = ['M', 'T', 'N', 'X'];
        const slots: SlotGradeProf[] = [];
        for (const h of rows) {
          if (h.tipo && h.tipo !== 'AULA') continue;
          if (!h.id) continue;
          const num = Number(h.numeroAula);
          if (!num || num < 1) continue;
          if (!h.diaSemana || !diasSemanaNoPeriodo.has(h.diaSemana)) continue;
          const turnoKey = turnoKeyFromNome(h.turma?.turnoOperacional?.nome);
          slots.push({
            id: h.id,
            diaSemana: h.diaSemana,
            turnoKey,
            turnoLabel: TURNO_LABEL[turnoKey],
            num,
            hora: h.horaInicio ? String(h.horaInicio).slice(0, 5) : '',
            horaFim: h.horaFim ? String(h.horaFim).slice(0, 5) : '',
            materia: h.materia?.nome || 'Aula',
            corVisual: h.materia?.corVisual || null,
            turma: h.turma?.nome || 'Turma',
            jaCoberto: cobertosIds.has(h.id),
          });
        }
        slots.sort((a, b) => {
          const da = DIAS_ORDEM.indexOf(a.diaSemana as (typeof DIAS_ORDEM)[number]);
          const db = DIAS_ORDEM.indexOf(b.diaSemana as (typeof DIAS_ORDEM)[number]);
          if (da !== db) return da - db;
          const ta = turnoOrd.indexOf(a.turnoKey);
          const tb = turnoOrd.indexOf(b.turnoKey);
          if (ta !== tb) return ta - tb;
          return a.num - b.num;
        });
        const livres = slots.filter((s) => !s.jaCoberto).map((s) => s.id);
        if (!cancelled) {
          setSlotsGrade(slots);
          setSlotsSelIds(livres);
        }
      } catch {
        if (!cancelled) {
          setSlotsGrade([]);
          setSlotsSelIds([]);
        }
      } finally {
        if (!cancelled) setLoadingAulas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [escolaId, professorId, dataInicio, dataFim]);

  const criarAfastamento = async () => {
    if (!escolaId || !professorId) {
      toast.error('Selecione o professor');
      return;
    }
    const livres = slotsGrade.filter((o) => !o.jaCoberto);
    if (livres.length > 0 && slotsSelIds.length === 0) {
      toast.error('Selecione ao menos um horário livre para cobrir');
      return;
    }
    if (livres.length === 0 && slotsGrade.some((o) => o.jaCoberto)) {
      toast.error(
        'Todos os horários deste período já têm cobertura. Abra o afastamento ativo para manutenção.',
      );
      return;
    }
    setSaving(true);
    try {
      const idsValidos = slotsSelIds.filter((id) =>
        slotsGrade.some((s) => s.id === id && !s.jaCoberto),
      );
      const { data } = await api.post(ROUTES.cobertura.afastamentos(escolaId), {
        professorId,
        tipo,
        dataInicio,
        dataFim: dataFim || dataInicio,
        motivo: motivo.trim() || undefined,
        ...(idsValidos.length ? { horarioAulaIds: idsValidos } : {}),
      });
      const n = data?.alertasGerados ?? 0;
      if (n === 0) {
        toast.warning(
          'Afastamento criado, mas sem slots no período (fim de semana, professor sem grade ou horários desmarcados).',
        );
      } else {
        toast.success(`Afastamento criado · ${n} slot(s) a cobrir`);
      }
      setMotivo('');
      await carregar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao criar afastamento');
    } finally {
      setSaving(false);
    }
  };

  const slotsLivres = useMemo(
    () => slotsGrade.filter((o) => !o.jaCoberto),
    [slotsGrade],
  );

  const turnosGradeForm = useMemo(() => {
    const order: TurnoKey[] = ['M', 'T', 'N', 'X'];
    return order
      .filter((tk) => slotsGrade.some((s) => s.turnoKey === tk))
      .map((tk) => {
        const doTurno = slotsGrade.filter((s) => s.turnoKey === tk);
        const dias = DIAS_ORDEM.filter((d) => doTurno.some((s) => s.diaSemana === d));
        const faixaMap = new Map<number, { num: number; hora: string }>();
        for (const s of doTurno) {
          if (!faixaMap.has(s.num)) {
            faixaMap.set(s.num, { num: s.num, hora: s.hora });
          }
        }
        const faixas = Array.from(faixaMap.values()).sort((a, b) => a.num - b.num);
        const bloco = TURNO_BLOCOS.find((b) => b.key === tk);
        return {
          key: tk,
          titulo: TURNO_LABEL[tk],
          accent: bloco?.accent || '#64748b',
          dias,
          faixas,
        };
      });
  }, [slotsGrade]);

  const slotsNaCelula = (turnoKey: TurnoKey, dia: string, num: number) =>
    slotsGrade.filter(
      (s) => s.turnoKey === turnoKey && s.diaSemana === dia && s.num === num,
    );

  const toggleSlotId = (id: string) => {
    const opt = slotsGrade.find((o) => o.id === id);
    if (opt?.jaCoberto) {
      toast.info('Este horário já tem cobertura — abra o afastamento ativo para manutenção.');
      return;
    }
    setSlotsSelIds((prev) =>
      prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id],
    );
  };

  const toggleTodasAulas = () => {
    if (!slotsLivres.length) return;
    const todas =
      slotsSelIds.length === slotsLivres.length &&
      slotsLivres.every((o) => slotsSelIds.includes(o.id));
    setSlotsSelIds(todas ? [] : slotsLivres.map((o) => o.id));
  };

  const solicitarCancelarAf = (id: string) => {
    setConfirmCancelarAfId(id);
  };

  const executarCancelarAf = async () => {
    if (!escolaId || !confirmCancelarAfId) return;
    setCancelandoAf(true);
    try {
      await api.post(ROUTES.cobertura.cancelarAfastamento(escolaId, confirmCancelarAfId));
      toast.success('Afastamento cancelado · coberturas do período revogadas');
      setAlertaSel(null);
      setCandidatos([]);
      setAfastamentoDetalheId(null);
      setConfirmCancelarAfId(null);
      await carregar({ forceAll: true });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao cancelar');
    } finally {
      setCancelandoAf(false);
    }
  };

  const abrirCandidatos = async (alertaId: string) => {
    if (!escolaId) return;
    setAlertaSel(alertaId);
    setSubId('');
    try {
      const { data } = await api.get(ROUTES.cobertura.candidatos(escolaId, alertaId));
      setCandidatos(Array.isArray(data?.candidatos) ? data.candidatos : []);
      if (!data?.candidatos?.length) {
        toast.info('Nenhum professor livre neste horário');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao buscar candidatos');
      setCandidatos([]);
    }
  };

  const alocar = async () => {
    if (!escolaId || !alertaSel || !subId) return;
    setAlocando(true);
    try {
      const jaCoberto = alertas.some(
        (a) => a.id === alertaSel && a.status === 'SUBTITUTO_ALOCADO',
      );
      await api.post(ROUTES.cobertura.alocar(escolaId, alertaSel), {
        professorSubstitutoId: subId,
      });
      toast.success(
        jaCoberto
          ? 'Substituto trocado · grade do dia atualizada'
          : 'Substituto alocado · no dia afetado o slot aparece na grade dele para chamada',
      );
      setAlertaSel(null);
      setSubId('');
      setCandidatos([]);
      await carregar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao alocar');
    } finally {
      setAlocando(false);
    }
  };

  const revogarAlerta = async () => {
    if (!escolaId || !alertaSel) return;
    setRevogando(true);
    try {
      await api.post(ROUTES.cobertura.revogarAlerta(escolaId, alertaSel));
      toast.success('Cobertura revogada · slot voltou a pendente');
      setAlertaSel(null);
      setSubId('');
      setCandidatos([]);
      await carregar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao revogar');
    } finally {
      setRevogando(false);
    }
  };

  /** Alertas do escopo (geral ou do afastamento) filtrados pelo card de substituto. */
  const alertasVisiveis = useMemo(() => {
    if (!filtroSubstitutoId) return alertas;
    return alertas.filter((a) => a.substituto?.id === filtroSubstitutoId);
  }, [alertas, filtroSubstitutoId]);

  const pendentes = useMemo(
    () => alertas.filter((a) => a.status === 'PENDENTE'),
    [alertas],
  );
  const pendenteIdsKey = useMemo(
    () => pendentes.map((a) => a.id).join(','),
    [pendentes],
  );
  const cobertos = alertas.filter((a) => a.status === 'SUBTITUTO_ALOCADO');
  const alertaSelObj = alertas.find((a) => a.id === alertaSel) || null;

  const substitutosCards = useMemo(() => {
    const map = new Map<string, { id: string; nome: string; slots: number }>();
    for (const a of alertas) {
      if (a.status !== 'SUBTITUTO_ALOCADO' || !a.substituto?.id) continue;
      const cur = map.get(a.substituto.id) || {
        id: a.substituto.id,
        nome: a.substituto.nome,
        slots: 0,
      };
      cur.slots += 1;
      map.set(a.substituto.id, cur);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.nome.localeCompare(b.nome, 'pt-BR'),
    );
  }, [alertas]);

  const candidatoOptions = useMemo(
    () => candidatos.map((c) => ({ value: c.id, label: c.nome })),
    [candidatos],
  );

  const turmasAfetadas = useMemo(() => {
    const map = new Map<string, TurmaAfetada>();
    for (const a of alertasVisiveis) {
      const id = a.turma?.id || '_sem';
      const turnoNome = a.turma?.turnoOperacional?.nome || '';
      const cur =
        map.get(id) ||
        ({
          id,
          nome: a.turma?.nome || 'Turma',
          turnoKey: turnoKeyFromNome(turnoNome),
          turnoNome: turnoNome || '—',
          pendentes: 0,
          cobertos: 0,
        } satisfies TurmaAfetada);
      if (a.status === 'PENDENTE') cur.pendentes += 1;
      if (a.status === 'SUBTITUTO_ALOCADO') cur.cobertos += 1;
      map.set(id, cur);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.pendentes !== b.pendentes) return b.pendentes - a.pendentes;
      return a.nome.localeCompare(b.nome, 'pt-BR');
    });
  }, [alertasVisiveis]);

  const turmasPorTurno = useMemo(() => {
    const groups = new Map<TurnoKey, TurmaAfetada[]>();
    for (const t of turmasAfetadas) {
      const list = groups.get(t.turnoKey) || [];
      list.push(t);
      groups.set(t.turnoKey, list);
    }
    return TURNO_BLOCOS.map((b) => ({
      ...b,
      turmas: groups.get(b.key) || [],
    })).filter((b) => b.turmas.length > 0);
  }, [turmasAfetadas]);

  const semanasDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const a of alertasVisiveis) {
      const ymd = a.dataAfetada.slice(0, 10);
      if (weekdayFromYmd(ymd) === 'DOMINGO') continue;
      set.add(mondayOf(ymd));
    }
    if (!set.size && afDetalhe) {
      set.add(mondayOf(afDetalhe.dataInicio.slice(0, 10)));
    }
    if (!set.size) {
      for (const a of afastamentos) {
        set.add(mondayOf(a.dataInicio.slice(0, 10)));
      }
    }
    return Array.from(set).sort();
  }, [alertasVisiveis, afastamentos, afDetalhe]);

  useEffect(() => {
    if (!semanasDisponiveis.length) {
      setSemanaInicio('');
      return;
    }
    if (!semanaInicio || !semanasDisponiveis.includes(semanaInicio)) {
      setSemanaInicio(semanasDisponiveis[0]);
    }
  }, [semanasDisponiveis, semanaInicio]);

  /** Data civil de cada coluna na semana selecionada. */
  const ymdPorDia = useMemo(() => {
    const map = new Map<string, string>();
    if (!semanaInicio) return map;
    for (const dia of DIAS_ORDEM) {
      const idx = DIAS_ORDEM.indexOf(dia);
      map.set(dia, addDaysYmd(semanaInicio, idx));
    }
    return map;
  }, [semanaInicio]);

  type CellTurmaItem = {
    turmaId: string;
    turmaNome: string;
    horaInicio: string;
    horaFim: string;
    materiaNome: string;
    titularNome: string;
    alerta: AlertaCobertura;
  };

  /** Grade só com dados dos alertas — sem fetch por turma (evita N+1 e travamento). */
  const buildTurnoGradeModel = useCallback(
    (turmas: TurmaAfetada[]) => {
      const turmaIds = new Set(turmas.map((t) => t.id));
      const turmaNomeById = new Map(turmas.map((t) => [t.id, t.nome]));
      // Seg–Sex da semana selecionada (sempre mostra Segunda, mesmo sem itens)
      const dias = [...DIAS_ORDEM.slice(0, 5)];
      const weekYmds = new Set(
        dias.map((d) => ymdPorDia.get(d)).filter((ymd): ymd is string => !!ymd),
      );

      const coberturaItens: Array<CellTurmaItem & { diaSemana: string; numeroAula: number }> = [];
      for (const a of alertasVisiveis) {
        if (a.status !== 'PENDENTE' && a.status !== 'SUBTITUTO_ALOCADO') continue;
        // Normaliza data civil (evita ISO com fuso cair no dia errado)
        const ymd = String(a.dataAfetada || '').slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd) || !weekYmds.has(ymd)) continue;
        const turmaId = a.turma?.id;
        if (!turmaId || !turmaIds.has(turmaId)) continue;

        // Coluna pela DATA do alerta (não pelo diaSemana da grade permanente)
        const diaSemana = weekdayFromYmd(ymd);
        if (!dias.includes(diaSemana as (typeof DIAS_ORDEM)[number])) continue;
        const numeroAula = a.numeroAula;

        coberturaItens.push({
          turmaId,
          turmaNome: turmaNomeById.get(turmaId) || a.turma?.nome || 'Turma',
          horaInicio: a.horaInicio ? String(a.horaInicio).slice(0, 5) : '',
          horaFim: a.horaFim ? String(a.horaFim).slice(0, 5) : '',
          materiaNome: a.materia?.nome || `${numeroAula}ª`,
          titularNome: a.titular?.nome || '—',
          alerta: a,
          diaSemana,
          numeroAula,
        });
      }

      const nums = [...new Set(coberturaItens.map((x) => x.numeroAula))]
        .filter((n) => n > 0)
        .sort((a, b) => a - b);
      const linhasAula = nums.map((num) => {
        const sample = coberturaItens.find((x) => x.numeroAula === num);
        return {
          num,
          hora: sample?.horaInicio || '',
          horaFim: sample?.horaFim || '',
        };
      });

      const itensPorDiaAula = new Map<string, CellTurmaItem[]>();
      for (const item of coberturaItens) {
        const key = `${item.diaSemana}|${item.numeroAula}`;
        const list = itensPorDiaAula.get(key) || [];
        list.push(item);
        itensPorDiaAula.set(key, list);
      }
      for (const [, list] of itensPorDiaAula) {
        list.sort((a, b) => a.turmaNome.localeCompare(b.turmaNome, 'pt-BR'));
      }

      return { dias, linhasAula, itensPorDiaAula };
    },
    [alertasVisiveis, ymdPorDia],
  );

  const semanaIdx = Math.max(0, semanasDisponiveis.indexOf(semanaInicio));
  const podeSemanaAnt = semanaIdx > 0;
  const podeSemanaProx = semanaIdx >= 0 && semanaIdx < semanasDisponiveis.length - 1;

  /** Carrega ranking: quantidade na frente, zero some da lista. */
  useEffect(() => {
    if (!escolaId || !pendenteIdsKey) {
      setLoteRanking([]);
      setLoteRankingLoading(false);
      return;
    }
    const alertaIds = pendenteIdsKey.split(',').filter(Boolean);
    let cancelled = false;
    setLoteRankingLoading(true);
    void (async () => {
      try {
        const { data } = await api.post(ROUTES.cobertura.rankingEncaixe(escolaId), {
          alertaIds,
        });
        if (cancelled) return;
        const rows = (Array.isArray(data?.ranking) ? data.ranking : []) as RankEncaixe[];
        setLoteRanking(rows);
      } catch {
        if (!cancelled) setLoteRanking([]);
      } finally {
        if (!cancelled) setLoteRankingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [escolaId, pendenteIdsKey]);

  const loteProfessorOptions = useMemo(
    () =>
      loteRanking.map((r) => ({
        value: r.id,
        label: `${r.quantidade} · ${r.nome}`,
      })),
    [loteRanking],
  );

  const loteEncaixeIds = useMemo(() => {
    if (!loteSubId) return new Set<string>();
    const row = loteRanking.find((r) => r.id === loteSubId);
    return new Set(row?.encaixam ?? []);
  }, [loteSubId, loteRanking]);

  // Se o selecionado sumiu do ranking (0 encaixes), limpa
  useEffect(() => {
    if (!loteSubId) return;
    if (loteRankingLoading) return;
    if (!loteRanking.some((r) => r.id === loteSubId)) {
      setLoteSubId('');
    }
  }, [loteSubId, loteRanking, loteRankingLoading]);

  const alocarLote = async (modo: 'AUTO' | 'MESMO_PROFESSOR') => {
    if (!escolaId) return;
    if (!pendentes.length) {
      toast.info('Não há slots pendentes');
      return;
    }
    if (modo === 'MESMO_PROFESSOR' && !loteSubId) {
      toast.error('Selecione o professor substituto do lote');
      return;
    }
    setLoteRunning(true);
    try {
      const { data } = await api.post(ROUTES.cobertura.alocarLote(escolaId), {
        modo,
        ...(modo === 'MESMO_PROFESSOR'
          ? { professorSubstitutoId: loteSubId }
          : {}),
        alertaIds: pendentes.map((a) => a.id),
      });
      const ok = data?.alocados ?? 0;
      const skip = data?.pulados ?? 0;
      if (ok === 0) {
        toast.warning(
          `Nenhum slot alocado${skip ? ` (${skip} sem livre compatível)` : ''}.`,
        );
      } else {
        toast.success(
          `Lote: ${ok} alocado${ok === 1 ? '' : 's'}${skip ? ` · ${skip} pulado${skip === 1 ? '' : 's'} (conflito)` : ''}`,
        );
      }
      setAlertaSel(null);
      setCandidatos([]);
      setLoteSubId(''); // limpa prévia — evita destacar tarde/noite após aplicar
      await carregar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro na alocação em lote');
    } finally {
      setLoteRunning(false);
    }
  };

  return (
    <div className="admin-page" style={{ marginTop: -24, paddingBottom: 32 }}>
      <div style={{ marginBottom: 16 }}>
        {afDetalhe ? (
          <>
            <button
              type="button"
              onClick={() => {
                setAfastamentoDetalheId(null);
                void carregar({ forceAll: true });
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                border: 'none',
                background: 'transparent',
                color: '#4f46e5',
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer',
                padding: 0,
                marginBottom: 8,
              }}
            >
              <ArrowLeft size={16} /> Voltar
            </button>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserMinus size={22} /> {afDetalhe.professor.nome}
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Manutenção da cobertura · {formatYmdBr(afDetalhe.dataInicio)}
              {afDetalhe.dataFim !== afDetalhe.dataInicio
                ? ` → ${formatYmdBr(afDetalhe.dataFim)}`
                : ''}
              {' · '}
              {STATUS_LABEL[afDetalhe.status] || afDetalhe.status}
              {afDetalhe.motivo ? ` · ${afDetalhe.motivo}` : ''}
            </p>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserMinus size={22} /> Cobertura de grade
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Professor falta → gera alertas nos slots → aloca substituto livre. No fim do período o titular volta sozinho
              (grade permanente não muda).
              {isSimulation ? (
                <span style={{ marginLeft: 8, color: '#7c3aed', fontWeight: 700 }}>
                  <Calendar size={12} style={{ display: 'inline', verticalAlign: -2 }} /> Dia sim: {hojeStr}
                </span>
              ) : null}
            </p>
          </>
        )}
      </div>

      {!afDetalhe ? (
        <>
          {/* Form afastamento */}
          <section
            style={{
              background: 'var(--bg-card, #fff)',
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <UserPlus size={16} /> Registrar afastamento
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 10,
                alignItems: 'end',
              }}
            >
              <div>
                <Dropdown
                  label="Professor"
                  options={professorOptions}
                  value={professorId}
                  onChange={(v) => setProfessorId(String(v || ''))}
                  placeholder={
                    profIdsComGrade.size
                      ? 'Buscar professor com grade…'
                      : 'Buscar professor…'
                  }
                  searchable
                  clearable
                  triggerIcon={<User size={14} />}
                  width="100%"
                  maxVisible={8}
                />
              </div>
              <Dropdown
                label="Tipo"
                options={tipoOptions}
                value={tipo}
                onChange={(v) => setTipo(String(v || 'IMPREVISTO'))}
                placeholder="Selecione…"
                width="100%"
              />
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'end',
                  flexShrink: 0,
                  minWidth: 'max-content',
                }}
              >
                <DatePicker
                  label="Início"
                  value={dataInicio}
                  onChange={(ymd) => {
                    setDataInicio(ymd);
                    if (ymd && ymd > dataFim) setDataFim(ymd);
                  }}
                  width={168}
                />
                <DatePicker
                  label="Fim"
                  value={dataFim}
                  min={dataInicio}
                  onChange={(ymd) => setDataFim(ymd)}
                  width={168}
                />
              </div>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  gridColumn: '1 / -1',
                }}
              >
                Motivo (opcional)
                <input
                  className="form-input"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: atestado médico"
                  style={{ height: 36 }}
                />
              </label>

              {professorId ? (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      marginBottom: 4,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ fontSize: '0.7rem', fontWeight: 700 }}>Horários a cobrir</div>
                    {slotsLivres.length > 0 ? (
                      <button
                        type="button"
                        onClick={toggleTodasAulas}
                        style={{
                          height: 22,
                          padding: '0 8px',
                          borderRadius: 999,
                          border:
                            slotsSelIds.length === slotsLivres.length
                              ? '1px solid #4f46e5'
                              : '1px solid #e2e8f0',
                          background:
                            slotsSelIds.length === slotsLivres.length ? '#eef2ff' : '#fff',
                          color:
                            slotsSelIds.length === slotsLivres.length ? '#4338ca' : '#64748b',
                          fontSize: '0.6rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        Todas livres
                      </button>
                    ) : null}
                  </div>
                  <div style={{ fontSize: '0.6rem', color: '#64748b', marginBottom: 6 }}>
                    Por turno · cor da matéria · clique para incluir/excluir · verde = já coberto
                  </div>
                  {loadingAulas ? (
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Carregando horários…</div>
                  ) : !slotsGrade.length ? (
                    <div style={{ fontSize: '0.65rem', color: '#b45309' }}>
                      Nenhuma aula deste professor nos dias selecionados.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${Math.min(
                            turnosGradeForm.filter((t) => t.key !== 'X').length || 1,
                            3,
                          )}, minmax(0, 1fr))`,
                          gap: 8,
                          alignItems: 'start',
                        }}
                      >
                        {turnosGradeForm.map((turno) => (
                          <div
                            key={turno.key}
                            style={{
                              border: '1px solid #e2e8f0',
                              borderRadius: 8,
                              padding: 6,
                              background: '#fff',
                              minWidth: 0,
                              gridColumn: turno.key === 'X' ? '1 / -1' : undefined,
                            }}
                          >
                            <div
                              style={{
                                fontSize: '0.62rem',
                                fontWeight: 800,
                                color: turno.accent,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                marginBottom: 4,
                                paddingBottom: 3,
                                borderBottom: `2px solid ${turno.accent}`,
                              }}
                            >
                              {turno.titulo}
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: `58px repeat(${turno.dias.length}, minmax(64px, 1fr))`,
                                  columnGap: 6,
                                  rowGap: 3,
                                  minWidth: 58 + turno.dias.length * 64,
                                }}
                              >
                                <div />
                                {turno.dias.map((d) => (
                                  <div
                                    key={d}
                                    style={{
                                      textAlign: 'center',
                                      fontSize: '0.55rem',
                                      fontWeight: 800,
                                      color: '#64748b',
                                      textTransform: 'uppercase',
                                      lineHeight: 1.1,
                                      paddingBottom: 2,
                                    }}
                                  >
                                    {DIA_CURTO[d] || d}
                                  </div>
                                ))}
                                {turno.faixas.map((faixa) => (
                                  <div key={`${turno.key}-${faixa.num}`} style={{ display: 'contents' }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        gap: 4,
                                        paddingRight: 2,
                                        minWidth: 58,
                                        boxSizing: 'border-box',
                                      }}
                                      title={`${faixa.num}ª aula${faixa.hora ? ` · ${faixa.hora}` : ''}`}
                                    >
                                      <span
                                        style={{
                                          flex: '0 0 18px',
                                          width: 18,
                                          textAlign: 'right',
                                          fontSize: '0.62rem',
                                          fontWeight: 800,
                                          color: '#0f172a',
                                          lineHeight: 1,
                                          fontVariantNumeric: 'tabular-nums',
                                        }}
                                      >
                                        {faixa.num}ª
                                      </span>
                                      {faixa.hora ? (
                                        <span
                                          style={{
                                            fontSize: '0.58rem',
                                            fontWeight: 700,
                                            color: '#0f172a',
                                            fontVariantNumeric: 'tabular-nums',
                                            fontFamily:
                                              'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                            letterSpacing: '-0.02em',
                                            lineHeight: 1,
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {faixa.hora}
                                        </span>
                                      ) : null}
                                    </div>
                                    {turno.dias.map((dia) => {
                                      const cellSlots = slotsNaCelula(turno.key, dia, faixa.num);
                                      if (!cellSlots.length) {
                                        return (
                                          <div
                                            key={`${turno.key}-${faixa.num}-${dia}`}
                                            style={{
                                              minHeight: 30,
                                              borderRadius: 4,
                                              background: '#f8fafc',
                                              border: '1px dashed #eef2f7',
                                            }}
                                          />
                                        );
                                      }
                                      return (
                                        <div
                                          key={`${turno.key}-${faixa.num}-${dia}`}
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            minHeight: 30,
                                            minWidth: 0,
                                          }}
                                        >
                                          {cellSlots.map((s) => {
                                            const on = slotsSelIds.includes(s.id);
                                            const cor = s.corVisual || '#64748b';
                                            if (s.jaCoberto) {
                                              return (
                                                <button
                                                  key={s.id}
                                                  type="button"
                                                  onClick={() => toggleSlotId(s.id)}
                                                  style={{
                                                    minHeight: 30,
                                                    padding: '3px 5px',
                                                    borderRadius: 4,
                                                    border: '1px solid #86efac',
                                                    background: '#f0fdf4',
                                                    color: '#14532d',
                                                    cursor: 'default',
                                                    textAlign: 'left',
                                                    lineHeight: 1.2,
                                                    boxShadow: `inset 2px 0 0 0 ${cor}`,
                                                    minWidth: 0,
                                                    width: '100%',
                                                  }}
                                                  title="Já tem alerta de cobertura neste slot"
                                                >
                                                  <div
                                                    style={{
                                                      fontSize: '0.5rem',
                                                      fontWeight: 700,
                                                      color: '#14532d',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                    }}
                                                  >
                                                    {s.materia}
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: '0.48rem',
                                                      fontWeight: 600,
                                                      color: '#166534',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                    }}
                                                  >
                                                    {s.turma} · ok
                                                  </div>
                                                </button>
                                              );
                                            }
                                            return (
                                              <button
                                                key={s.id}
                                                type="button"
                                                onClick={() => toggleSlotId(s.id)}
                                                style={{
                                                  minHeight: 30,
                                                  padding: '3px 5px',
                                                  borderRadius: 4,
                                                  border: on
                                                    ? `1px solid ${cor}`
                                                    : '1px solid #e2e8f0',
                                                  background: on
                                                    ? softMateriaBg(cor, 0.55)
                                                    : '#fff',
                                                  cursor: 'pointer',
                                                  textAlign: 'left',
                                                  lineHeight: 1.2,
                                                  boxShadow: on
                                                    ? `inset 2px 0 0 0 ${cor}`
                                                    : 'none',
                                                  minWidth: 0,
                                                  width: '100%',
                                                }}
                                                title={`${s.turnoLabel} · ${s.num}ª · ${s.materia} · ${s.turma}`}
                                              >
                                                <div
                                                  style={{
                                                    fontSize: '0.5rem',
                                                    fontWeight: on ? 800 : 700,
                                                    color: '#0f172a',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                  }}
                                                >
                                                  {s.materia}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: '0.48rem',
                                                    fontWeight: 600,
                                                    color: '#0f172a',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                  }}
                                                >
                                                  {s.turma}
                                                </div>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: '0.58rem', color: '#64748b' }}>
                        {slotsSelIds.length} de {slotsLivres.length} livre(s)
                        {slotsGrade.some((s) => s.jaCoberto)
                          ? ` · ${slotsGrade.filter((s) => s.jaCoberto).length} já cobertos`
                          : ''}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="btn-primary"
              disabled={
                saving ||
                !professorId ||
                (slotsLivres.length > 0 && slotsSelIds.length === 0) ||
                (slotsGrade.length > 0 && slotsLivres.length === 0)
              }
              onClick={() => void criarAfastamento()}
              style={{ marginTop: 12, height: 36, padding: '0 14px' }}
            >
              {saving ? 'Salvando…' : 'Criar e gerar alertas'}
            </button>
          </section>

          {/* Afastamentos — faixa compacta */}
          <section
            style={{
              background: 'var(--bg-card, #fff)',
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: 10,
              padding: '8px 10px',
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: '0.8rem' }}>Afastamentos ativos</div>
            {loading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Carregando…</div>
            ) : afastamentos.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Nenhum afastamento em andamento.</div>
            ) : (
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
                {afastamentos.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAfastamentoDetalheId(a.id)}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 7,
                      padding: '6px 8px',
                      fontSize: '0.7rem',
                      minWidth: 180,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    title="Abrir manutenção"
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.professor.nome}
                      </div>
                      <div style={{ color: '#64748b', marginTop: 1, fontSize: '0.65rem' }}>
                        {formatYmdBr(a.dataInicio)}
                        {a.dataFim !== a.dataInicio ? `→${formatYmdBr(a.dataFim)}` : ''}
                        {' · '}
                        {STATUS_LABEL[a.status] || a.status}
                      </div>
                    </div>
                    <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#4f46e5', flexShrink: 0 }}>
                      Abrir
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <section
          style={{
            background: 'var(--bg-card, #fff)',
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: 12,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
              {pendentes.length} pendente{pendentes.length === 1 ? '' : 's'} · {cobertos.length} coberto
              {cobertos.length === 1 ? '' : 's'}
            </div>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => solicitarCancelarAf(afDetalhe.id)}
              style={{ height: 32, padding: '0 12px', fontSize: '0.75rem' }}
            >
              Cancelar operação
            </button>
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.75rem', marginBottom: 8 }}>
            Professores cobrindo
          </div>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
            <button
              type="button"
              onClick={() => setFiltroSubstitutoId(null)}
              style={{
                minWidth: 88,
                flexShrink: 0,
                borderRadius: 8,
                border: !filtroSubstitutoId ? '1px solid #4f46e5' : '1px solid #e2e8f0',
                background: !filtroSubstitutoId ? '#eef2ff' : '#fff',
                padding: '8px 10px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 800, fontSize: '0.72rem', color: '#0f172a' }}>Todos</div>
              <div style={{ fontSize: '0.62rem', color: '#64748b', marginTop: 2 }}>
                {alertas.length} slot{alertas.length === 1 ? '' : 's'}
              </div>
            </button>
            {substitutosCards.map((p) => {
              const active = filtroSubstitutoId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setFiltroSubstitutoId(p.id)}
                  style={{
                    minWidth: 120,
                    flexShrink: 0,
                    borderRadius: 8,
                    border: active ? '1px solid #15803d' : '1px solid #e2e8f0',
                    background: active ? '#f0fdf4' : '#fff',
                    padding: '8px 10px',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      color: '#0f172a',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.nome}
                  </div>
                  <div style={{ fontSize: '0.62rem', color: '#64748b', marginTop: 2 }}>
                    {p.slots} slot{p.slots === 1 ? '' : 's'}
                  </div>
                </button>
              );
            })}
            {!substitutosCards.length ? (
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', alignSelf: 'center', padding: '0 4px' }}>
                Nenhum substituto alocado ainda
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* Lista geral: sem grade (só no detalhe). */}
      {!afDetalhe ? (
        <section
          style={{
            background: 'var(--bg-card, #fff)',
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            Resumo{' '}
            <span style={{ fontWeight: 500, color: '#64748b', fontSize: '0.75rem' }}>
              ({pendentes.length} pendente{pendentes.length === 1 ? '' : 's'} · {cobertos.length}{' '}
              coberto{cobertos.length === 1 ? '' : 's'})
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Abra um afastamento ativo acima para ver a grade e dar manutenção (trocar / revogar /
            cancelar operação).
          </p>
        </section>
      ) : null}

      {/* Detalhe — Grade / Lista */}
      {afDetalhe ? (
      <section
        style={{
          background: 'var(--bg-card, #fff)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 12,
          padding: 14,
          minHeight: 280,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div>
            <div style={{ fontWeight: 700 }}>
              Grade da cobertura{' '}
              <span style={{ fontWeight: 500, color: '#64748b', fontSize: '0.75rem' }}>
                ({pendentes.length} pendente{pendentes.length === 1 ? '' : 's'} · {cobertos.length} coberto
                {cobertos.length === 1 ? '' : 's'}
                {filtroSubstitutoId ? ` · filtro ativo` : ''})
              </span>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
              Clique no slot para trocar ou revogar o substituto.
            </div>
          </div>
          <div
            style={{
              display: 'inline-flex',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#f8fafc',
            }}
          >
            <button
              type="button"
              onClick={() => setViewMode('grade')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 32,
                padding: '0 12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 700,
                background: viewMode === 'grade' ? '#4f46e5' : 'transparent',
                color: viewMode === 'grade' ? '#fff' : '#64748b',
              }}
            >
              <LayoutGrid size={14} /> Grade
            </button>
            <button
              type="button"
              onClick={() => setViewMode('lista')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 32,
                padding: '0 12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 700,
                background: viewMode === 'lista' ? '#4f46e5' : 'transparent',
                color: viewMode === 'lista' ? '#fff' : '#64748b',
              }}
            >
              <List size={14} /> Lista
            </button>
          </div>
        </div>

        {pendentes.length > 0 && (
          <div
            style={{
              marginBottom: 12,
              padding: 12,
              borderRadius: 8,
              background: '#eef2ff',
              border: '1px solid #c7d2fe',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 8 }}>
              Alocação em lote ({pendentes.length} pendente{pendentes.length === 1 ? '' : 's'})
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(200px, 1fr) auto',
                gap: 8,
                alignItems: 'end',
              }}
            >
              <Dropdown
                options={loteProfessorOptions}
                value={loteSubId}
                onChange={(v) => setLoteSubId(String(v || ''))}
                placeholder={
                  loteRankingLoading
                    ? 'Calculando quem encaixa…'
                    : loteRanking.length
                      ? 'Quem mais encaixa (qtd · nome)…'
                      : 'Ninguém livre nos slots pendentes'
                }
                searchable
                clearable
                width="100%"
                maxVisible={10}
                triggerIcon={<User size={14} />}
                disabled={loteRankingLoading || loteRanking.length === 0}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={loteRunning || !loteSubId}
                  onClick={() => void alocarLote('MESMO_PROFESSOR')}
                  style={{ height: 34, padding: '0 12px', fontSize: '0.75rem' }}
                >
                  {loteRunning ? 'Alocando…' : 'Aplicar onde couber'}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  disabled={loteRunning}
                  onClick={() => void alocarLote('AUTO')}
                  style={{ height: 34, padding: '0 12px', fontSize: '0.75rem' }}
                  title="Em cada slot escolhe um livre, equilibrando a carga"
                >
                  Preencher automático
                </button>
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: '0.68rem', color: '#4338ca', fontWeight: 700 }}>
              {loteRankingLoading
                ? 'Montando ranking de encaixe…'
                : loteSubId && loteEncaixeIds.size > 0
                  ? `Prévia: encaixa em ${loteEncaixeIds.size} de ${pendentes.length} — destacados na grade`
                  : !loteRankingLoading
                    ? `${loteRanking.length} professor(es) com encaixe (zero não aparece)`
                    : null}
            </div>
          </div>
        )}

        {alertaSel && (
          <div
            style={{
              marginBottom: 12,
              padding: 12,
              borderRadius: 8,
              background: alertaSelObj?.status === 'SUBTITUTO_ALOCADO' ? '#f0fdf4' : '#fffbeb',
              border:
                alertaSelObj?.status === 'SUBTITUTO_ALOCADO'
                  ? '1px solid #86efac'
                  : '1px solid #fbbf24',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 4 }}>
              {alertaSelObj?.status === 'SUBTITUTO_ALOCADO'
                ? 'Manutenção do slot'
                : 'Alocar substituto'}{' '}
              ({candidatos.length} livre{candidatos.length === 1 ? '' : 's'})
            </div>
            {alertaSelObj ? (
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 8 }}>
                {formatYmdBr(alertaSelObj.dataAfetada)} · {alertaSelObj.numeroAula}ª ·{' '}
                {alertaSelObj.turma?.nome || 'Turma'}
                {alertaSelObj.materia?.nome ? ` · ${alertaSelObj.materia.nome}` : ''}
                {alertaSelObj.substituto
                  ? ` · atual: ${alertaSelObj.substituto.nome}`
                  : ''}
              </div>
            ) : null}
            <div style={{ marginBottom: 8, maxWidth: 420 }}>
              <Dropdown
                options={candidatoOptions}
                value={subId}
                onChange={(v) => setSubId(String(v || ''))}
                placeholder="Buscar professor livre…"
                searchable
                clearable
                width="100%"
                maxVisible={8}
                triggerIcon={<User size={14} />}
              />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button
                type="button"
                className="btn-primary"
                disabled={!subId || alocando || revogando}
                onClick={() => void alocar()}
                style={{ height: 32, padding: '0 12px', fontSize: '0.75rem' }}
              >
                {alocando
                  ? 'Salvando…'
                  : alertaSelObj?.status === 'SUBTITUTO_ALOCADO'
                    ? 'Trocar professor'
                    : 'Confirmar'}
              </button>
              {alertaSelObj?.status === 'SUBTITUTO_ALOCADO' ? (
                <button
                  type="button"
                  className="btn-cancel"
                  disabled={alocando || revogando}
                  onClick={() => void revogarAlerta()}
                  style={{ height: 32, padding: '0 12px', fontSize: '0.75rem' }}
                >
                  {revogando ? 'Revogando…' : 'Revogar cobertura'}
                </button>
              ) : null}
              <button
                type="button"
                className="btn-cancel"
                disabled={alocando || revogando}
                onClick={() => {
                  setAlertaSel(null);
                  setSubId('');
                  setCandidatos([]);
                }}
                style={{ height: 32, padding: '0 12px', fontSize: '0.75rem' }}
              >
                Fechar
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Carregando…</div>
        ) : alertasVisiveis.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            {filtroSubstitutoId
              ? 'Nenhum slot deste professor no filtro.'
              : 'Nenhum alerta de cobertura.'}
          </div>
        ) : viewMode === 'grade' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Navegação de semana */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <button
                type="button"
                disabled={!podeSemanaAnt}
                onClick={() => {
                  if (!podeSemanaAnt) return;
                  setSemanaInicio(semanasDisponiveis[semanaIdx - 1]);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  background: podeSemanaAnt ? '#fff' : '#f8fafc',
                  color: podeSemanaAnt ? '#0f172a' : '#cbd5e1',
                  cursor: podeSemanaAnt ? 'pointer' : 'default',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Semana anterior"
              >
                <ChevronLeft size={16} />
              </button>
              <div style={{ textAlign: 'center', minWidth: 160 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#0f172a' }}>
                  {semanaInicio
                    ? `${formatYmdBr(semanaInicio)} – ${formatYmdBr(addDaysYmd(semanaInicio, 4))}`
                    : '—'}
                </div>
                <div style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                  {semanasDisponiveis.length > 1
                    ? `Semana ${semanaIdx + 1} de ${semanasDisponiveis.length}`
                    : 'Semana da cobertura'}
                </div>
              </div>
              <button
                type="button"
                disabled={!podeSemanaProx}
                onClick={() => {
                  if (!podeSemanaProx) return;
                  setSemanaInicio(semanasDisponiveis[semanaIdx + 1]);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  background: podeSemanaProx ? '#fff' : '#f8fafc',
                  color: podeSemanaProx ? '#0f172a' : '#cbd5e1',
                  cursor: podeSemanaProx ? 'pointer' : 'default',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Próxima semana"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {turmasPorTurno.map((bloco) => {
              const model = buildTurnoGradeModel(bloco.turmas);
              const pendTurno = bloco.turmas.reduce((s, t) => s + t.pendentes, 0);

              return (
                <div
                  key={bloco.key}
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 10px',
                      background: '#f8fafc',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        color: bloco.accent,
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                      }}
                    >
                      {bloco.titulo}
                    </span>
                    <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                      {bloco.turmas.length} turma{bloco.turmas.length === 1 ? '' : 's'} no horário
                      {pendTurno > 0 ? ` · ${pendTurno} a cobrir` : ''}
                    </span>
                  </div>

                  <div style={{ padding: 8 }}>
                    {!model.linhasAula.length ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', padding: 12 }}>
                        Nenhum horário de cobertura nesta semana.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            fontSize: '0.7rem',
                            minWidth: 560,
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: 'left',
                                  padding: '6px 8px',
                                  background: '#f8fafc',
                                  borderBottom: '1px solid #e2e8f0',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1,
                                }}
                              >
                                Horário
                              </th>
                              {model.dias.map((dia) => {
                                const ymd = ymdPorDia.get(dia);
                                return (
                                  <th
                                    key={dia}
                                    style={{
                                      textAlign: 'center',
                                      padding: '6px 4px',
                                      background: '#f8fafc',
                                      borderBottom: '1px solid #e2e8f0',
                                      fontWeight: 700,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <div>{DIA_CURTO[dia] || dia}</div>
                                    {ymd ? (
                                      <div style={{ fontWeight: 500, color: '#64748b', fontSize: '0.62rem' }}>
                                        {formatYmdBr(ymd)}
                                      </div>
                                    ) : null}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {model.linhasAula.map(({ num, hora, horaFim }) => (
                              <tr key={`${bloco.key}-${num}`}>
                                <td
                                  style={{
                                    padding: '6px 8px',
                                    borderBottom: '1px solid #f1f5f9',
                                    fontWeight: 700,
                                    background: '#fff',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                    whiteSpace: 'nowrap',
                                    verticalAlign: 'top',
                                  }}
                                >
                                  {num}ª
                                  {hora ? (
                                    <span
                                      style={{
                                        display: 'block',
                                        fontWeight: 500,
                                        color: '#94a3b8',
                                        fontSize: '0.6rem',
                                      }}
                                    >
                                      {hora}
                                      {horaFim ? `–${horaFim}` : ''}
                                    </span>
                                  ) : null}
                                </td>
                                {model.dias.map((dia) => {
                                  const itens = model.itensPorDiaAula.get(`${dia}|${num}`) || [];

                                  if (!itens.length) {
                                    return (
                                      <td
                                        key={`${dia}|${num}`}
                                        style={{
                                          padding: 3,
                                          borderBottom: '1px solid #f1f5f9',
                                          background: '#fafafa',
                                        }}
                                      />
                                    );
                                  }

                                  return (
                                    <td
                                      key={`${dia}|${num}`}
                                      style={{
                                        padding: 3,
                                        borderBottom: '1px solid #f1f5f9',
                                        verticalAlign: 'top',
                                      }}
                                    >
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {itens.map((item) => {
                                          const alerta = item.alerta;
                                          const pendente = alerta.status === 'PENDENTE';
                                          const coberto = alerta.status === 'SUBTITUTO_ALOCADO';
                                          const materiaNome = item.materiaNome || `${num}ª`;
                                          const profNome = coberto
                                            ? alerta.substituto?.nome || 'Substituto'
                                            : item.titularNome;
                                          const previewOn = !!loteSubId && !loteRankingLoading;
                                          const encaixa =
                                            !!pendente && previewOn && loteEncaixeIds.has(alerta.id);
                                          const foraDaPrevia =
                                            previewOn && !!pendente && !loteEncaixeIds.has(alerta.id);

                                          return (
                                            <button
                                              key={`${item.turmaId}-${alerta.id}`}
                                              type="button"
                                              onClick={() => void abrirCandidatos(alerta.id)}
                                              style={{
                                                borderRadius: 6,
                                                border: encaixa
                                                  ? '2px solid #4f46e5'
                                                  : pendente
                                                    ? '1px solid #fbbf24'
                                                    : '1px solid #86efac',
                                                background: encaixa
                                                  ? '#eef2ff'
                                                  : pendente
                                                    ? '#fffbeb'
                                                    : '#f0fdf4',
                                                padding: '4px 5px',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                width: '100%',
                                                boxShadow: encaixa
                                                  ? '0 0 0 2px rgba(79, 70, 229, 0.35)'
                                                  : 'none',
                                                outline: encaixa ? '2px solid #818cf8' : 'none',
                                                outlineOffset: encaixa ? 1 : 0,
                                                opacity: foraDaPrevia ? 0.35 : 1,
                                                transform: encaixa ? 'scale(1.02)' : undefined,
                                                transition: 'box-shadow 0.15s, opacity 0.15s',
                                              }}
                                              title={
                                                encaixa
                                                  ? 'Prévia: este professor encaixa neste horário'
                                                  : foraDaPrevia
                                                    ? 'Prévia: não encaixa neste horário'
                                                    : undefined
                                              }
                                            >
                                              <div
                                                style={{
                                                  fontWeight: 800,
                                                  fontSize: '0.58rem',
                                                  color: pendente ? '#b45309' : '#15803d',
                                                  textTransform: 'uppercase',
                                                }}
                                              >
                                                {item.turmaNome}
                                                {pendente ? ' · cobrir' : ' · ok'}
                                              </div>
                                              <div
                                                style={{
                                                  fontWeight: 700,
                                                  color: '#0f172a',
                                                  marginTop: 1,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                  fontSize: '0.65rem',
                                                }}
                                              >
                                                {materiaNome}
                                              </div>
                                              <div
                                                style={{
                                                  color: '#64748b',
                                                  marginTop: 1,
                                                  fontSize: '0.58rem',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                }}
                                              >
                                                {profNome}
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
              Âmbar = cobrir · verde = coberto
              {loteSubId
                ? ' · anel roxo = prévia onde o professor do lote encaixa'
                : ''}
              . Use ← → se cruzar semanas.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 480, overflowY: 'auto' }}>
            {alertasVisiveis.map((a) => (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: '0.75rem',
                  background: a.status === 'SUBTITUTO_ALOCADO' ? '#f0fdf4' : '#fff',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>
                    {formatYmdBr(a.dataAfetada)} · {a.numeroAula}ª{' '}
                    {a.horaInicio ? `(${String(a.horaInicio).slice(0, 5)})` : ''}
                  </div>
                  <div style={{ color: '#64748b' }}>
                    {a.titular?.nome || 'Titular'} → {a.turma?.nome || 'Turma'}
                    {a.materia?.nome ? ` · ${a.materia.nome}` : ''}
                  </div>
                  {a.substituto ? (
                    <div style={{ color: '#15803d', fontWeight: 600, marginTop: 2 }}>
                      Substituto: {a.substituto.nome}
                    </div>
                  ) : null}
                </div>
                {a.status === 'PENDENTE' ? (
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ height: 30, padding: '0 10px', fontSize: '0.72rem', flexShrink: 0 }}
                    onClick={() => void abrirCandidatos(a.id)}
                  >
                    Alocar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void abrirCandidatos(a.id)}
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      color: '#15803d',
                      background: '#dcfce7',
                      padding: '3px 8px',
                      borderRadius: 999,
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Manter / trocar
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
      ) : null}

      <ConfirmModal
        open={!!confirmCancelarAfId}
        title="Cancelar Cobertura de Grade"
        message="Deseja realmente cancelar toda a operação? As coberturas do período serão revogadas e o professor titular retornará aos slots da grade."
        confirmLabel={cancelandoAf ? 'Cancelando...' : 'Sim, cancelar operação'}
        cancelLabel="Voltar"
        variant="danger"
        onConfirm={() => void executarCancelarAf()}
        onCancel={() => !cancelandoAf && setConfirmCancelarAfId(null)}
      />
    </div>
  );
}
